import { useMemo } from "react";
import { DateFilterOptions } from "../util/ChartUtil";
import {
  historicalQueueProductionQueryGenerator,
  caseChartDataMapper,
  getQueueFilterClausesWithoutFaxAutoAttach,
  workingTimeQueryGenerator,
  incomingQueryGenerator,
} from "../util/QueryUtil";
import {
  calculateTotalNumberOfCases,
  calculateTotalWorkingTime,
  calculateTotalIdleTime,
  mergeCaseCountDataForChart,
  mergeTimeWorkedDataForChart,
} from "../util/metricsUtil";
import { useGetElasticStats } from "hooks/useGetElasticStats";
import CaseCountChart from "../util/charts/CaseCountChart";
import TimeWorkedChart from "../util/charts/TimeWorkedChart";
import { User as QMAgent, Queue } from "@coherehealth/qm-api";
import ProductivityMetrics from "./ProductivityMetrics";
import { colorsLight, Caption } from "@coherehealth/common";

interface ProductivityContentProps {
  dateValue: DateFilterOptions;
  skills: string[];
  agents: QMAgent[];
  queue: Queue;
}

export default function ProductivityContent({ dateValue, skills, agents, queue }: ProductivityContentProps) {
  const { filters, filterTree, useFilterTree, id: queueId } = queue;
  // Create payloads for ElasticSearch queries
  const queueBasedSearchPayload = useMemo(() => {
    return historicalQueueProductionQueryGenerator(
      dateValue,
      getQueueFilterClausesWithoutFaxAutoAttach({ filters, filterTree, useFilterTree }),
      skills,
      agents
    );
  }, [agents, dateValue, filters, filterTree, useFilterTree, skills]);

  const todayQueueBasedSearchPayload = useMemo(() => {
    return historicalQueueProductionQueryGenerator(
      "1",
      getQueueFilterClausesWithoutFaxAutoAttach({ filters, filterTree, useFilterTree }),
      skills,
      agents
    );
  }, [agents, filters, filterTree, useFilterTree, skills]);

  const incomingPayload = useMemo(() => {
    return incomingQueryGenerator(
      dateValue,
      getQueueFilterClausesWithoutFaxAutoAttach({ filters, filterTree, useFilterTree }),
      skills
    );
  }, [dateValue, filters, filterTree, useFilterTree, skills]);

  const workingTimePayload = useMemo(() => {
    return workingTimeQueryGenerator(dateValue, agents, queueId || "");
  }, [agents, dateValue, queueId]);

  const todayWorkingTimePayload = useMemo(() => {
    return workingTimeQueryGenerator("1", agents, queueId || "");
  }, [agents, queueId]);

  // Query ElasticSearch
  const { data: productionDataUnaggregated } = useGetElasticStats(queueBasedSearchPayload);
  const productionData = caseChartDataMapper(productionDataUnaggregated?.aggregations?.dateCompleted, dateValue, true);

  const { data: todayProductionDataUnaggregated } = useGetElasticStats(todayQueueBasedSearchPayload);
  const todayProductionData = caseChartDataMapper(
    todayProductionDataUnaggregated?.aggregations?.dateCompleted,
    "1",
    true
  );

  const { data: workingTimeDataUnaggregated } = useGetElasticStats(workingTimePayload);
  const workingTimeData = caseChartDataMapper(workingTimeDataUnaggregated?.aggregations?.windowStart, dateValue, true);

  const { data: todayWorkingTimeDataUnaggregated } = useGetElasticStats(todayWorkingTimePayload);
  const todayWorkingTimeData = caseChartDataMapper(
    todayWorkingTimeDataUnaggregated?.aggregations?.windowStart,
    "1",
    true
  );

  const { data: incomingDataUnaggregated } = useGetElasticStats(incomingPayload);
  const incomingData = caseChartDataMapper(incomingDataUnaggregated?.aggregations?.dateCreated, dateValue, true);

  // Compute Productivity Metrics
  const skillFiltered = skills.length > 0 && skills[0] !== "All";
  const agentFiltered = agents.length > 0 && agents[0] !== ("All" as QMAgent);

  const totalProduction = useMemo(
    () => calculateTotalNumberOfCases(dateValue, productionData, todayProductionData, skillFiltered),
    [dateValue, productionData, todayProductionData, skillFiltered]
  );

  const totalHoursWorked = useMemo(
    () =>
      calculateTotalWorkingTime(
        dateValue,
        todayProductionData,
        productionData,
        todayWorkingTimeData,
        workingTimeData,
        skillFiltered
      ),
    [dateValue, todayProductionData, productionData, todayWorkingTimeData, workingTimeData, skillFiltered]
  );

  const totalHoursIdle = useMemo(
    () => calculateTotalIdleTime(dateValue, todayWorkingTimeData, workingTimeData, skillFiltered),
    [dateValue, todayWorkingTimeData, workingTimeData, skillFiltered]
  );

  // Merge data sources displayed on the same chart
  const caseCountChartData = mergeCaseCountDataForChart(incomingData, productionData, workingTimeData);
  const timeWorkedChartData = mergeTimeWorkedDataForChart(
    incomingData,
    todayProductionData,
    productionData,
    todayWorkingTimeData,
    workingTimeData,
    skillFiltered,
    dateValue
  );

  return (
    <div
      style={{
        margin: "55px 32px 0px",
        flex: 1,
        minWidth: 0,
        overflowX: "hidden",
      }}
    >
      <ProductivityMetrics production={totalProduction} workingTime={totalHoursWorked} idleTime={totalHoursIdle} />
      <div style={{ height: "200px", marginTop: "75px", marginRight: "-30px" }}>
        <CaseCountChart data={caseCountChartData} hourly={dateValue === "1"} agent={agentFiltered} />
      </div>
      <div style={{ height: "200px", margin: "50px -30px 50px 0px" }}>
        <TimeWorkedChart data={timeWorkedChartData} hourly={dateValue === "1"} />
      </div>
      <div
        style={{
          margin: "0px 70px",
          display: "inline-flex",
          justifyContent: "space-between",
          width: "calc(100% - 140px)",
        }}
      >
        <LegendItem color="#EA1D63" label="QPH" />
        <LegendItem color="#3E276A" label="Cases worked" />
        {!agentFiltered && <LegendItem color="#32C9A8" label="Incoming cases" />}
        {!skillFiltered && <LegendItem color="#B1B4B9" label="Idle time" />}
        <LegendItem color="#02718B" label="Working time" />
      </div>
    </div>
  );
}

interface LegendItemProps {
  color: string;
  label: string;
}
const LegendItem = ({ color, label }: LegendItemProps) => {
  return (
    <div style={{ display: "inline-flex" }}>
      <div style={{ width: "16px", height: "16px", backgroundColor: color }} />
      <Caption style={{ color: colorsLight.font.light, marginLeft: "5px" }}>{label}</Caption>
    </div>
  );
};
